import type { Question } from '../../types';
import { HAB_MH_CONDITION } from './shared-options';

// ---------------------------------------------------------------------------
// EPLI / Fair Housing Supplement
// Source: Centered Partners MHMF intake — "EPLI & Fair Housing" sheet.
// Habitational EPLI bundles fair housing / tenant discrimination — both
// gating items for institutional capital partners.
// ---------------------------------------------------------------------------

const EPLI_CONDITION = [
  { field: 'coverage_lines', operator: 'contains' as const, value: 'epli' },
  ...HAB_MH_CONDITION,
];

export const epliFairHousingQuestions: Question[] = [
  {
    id: 'epli_w2_count',
    text: 'Total W-2 employee count',
    section: 'employees',
    group: 'epli',
    sort_order: 600,
    field_type: 'number',
    placeholder: '12',
    required: true,
    field_path: 'epli.w2_count',
    conditions: EPLI_CONDITION,
    tags: ['epli', 'lob'],
  },
  {
    id: 'epli_states',
    text: 'Which states have employees?',
    section: 'employees',
    group: 'epli',
    sort_order: 610,
    field_type: 'state_select',
    required: true,
    field_path: 'epli.employee_states',
    conditions: EPLI_CONDITION,
    tags: ['epli', 'lob'],
  },
  {
    id: 'epli_turnover_pct',
    text: 'Annual employee turnover percentage',
    section: 'employees',
    group: 'epli',
    sort_order: 620,
    field_type: 'percentage',
    placeholder: '15',
    required: true,
    validation: { min: 0, max: 100 },
    field_path: 'epli.turnover_pct',
    conditions: EPLI_CONDITION,
    tags: ['epli', 'lob'],
  },
  {
    id: 'epli_handbook',
    text: 'Is there a current employee handbook?',
    section: 'employees',
    group: 'epli',
    sort_order: 630,
    field_type: 'boolean',
    required: true,
    field_path: 'epli.handbook',
    conditions: EPLI_CONDITION,
    tags: ['epli', 'lob'],
  },
  {
    id: 'epli_harassment_training',
    text: 'How often is anti-harassment / EEO training conducted?',
    section: 'employees',
    group: 'epli',
    sort_order: 640,
    field_type: 'select',
    options: [
      { label: 'Annually with records', value: 'annual' },
      { label: 'At hire only', value: 'at_hire' },
      { label: 'Informally / ad hoc', value: 'ad_hoc' },
      { label: 'Not conducted', value: 'none' },
    ],
    required: true,
    field_path: 'epli.harassment_training',
    conditions: EPLI_CONDITION,
    tags: ['epli', 'lob'],
  },
  {
    id: 'epli_fair_housing_training',
    text: 'Is fair housing training conducted?',
    subtext: 'NAA, NMHC, or similar provider — with completion records.',
    section: 'employees',
    group: 'epli',
    sort_order: 650,
    field_type: 'select',
    options: [
      { label: 'Yes — annually with records', value: 'annual' },
      { label: 'Yes — at hire only', value: 'at_hire' },
      { label: 'Informally / ad hoc', value: 'ad_hoc' },
      { label: 'Not conducted', value: 'none' },
    ],
    required: true,
    field_path: 'epli.fair_housing_training',
    conditions: EPLI_CONDITION,
    tags: ['epli', 'lob'],
  },
  {
    id: 'epli_screening_policy',
    text: 'Is there a written resident application & screening criteria policy?',
    section: 'employees',
    group: 'epli',
    sort_order: 660,
    field_type: 'boolean',
    required: true,
    field_path: 'epli.screening_policy',
    conditions: EPLI_CONDITION,
    tags: ['epli', 'lob'],
  },
  {
    id: 'epli_accommodation_log',
    text: 'Is a reasonable accommodation request log maintained?',
    subtext: 'Requests and outcomes for the last 3 years.',
    section: 'employees',
    group: 'epli',
    sort_order: 670,
    field_type: 'boolean',
    required: true,
    field_path: 'epli.accommodation_log',
    conditions: EPLI_CONDITION,
    tags: ['epli', 'lob'],
  },
  {
    id: 'epli_complaints',
    text: 'Any HUD, DOJ, or state fair housing complaints in the last 5 years?',
    section: 'employees',
    group: 'epli',
    sort_order: 680,
    field_type: 'boolean',
    required: true,
    field_path: 'epli.complaints',
    conditions: EPLI_CONDITION,
    tags: ['epli', 'lob'],
  },
  {
    id: 'epli_complaints_detail',
    text: 'Describe the complaints',
    subtext: 'Open and closed, with outcomes where resolved.',
    section: 'employees',
    group: 'epli',
    sort_order: 690,
    field_type: 'textarea',
    required: true,
    field_path: 'epli.complaints_detail',
    conditions: [...EPLI_CONDITION, { field: 'epli_complaints', operator: 'eq', value: true }],
    tags: ['epli', 'lob'],
  },
  {
    id: 'epli_ada_audit',
    text: 'Has a property accessibility (ADA) audit been completed?',
    section: 'employees',
    group: 'epli',
    sort_order: 700,
    field_type: 'select',
    options: [
      { label: 'Yes — within the last 3 years', value: 'recent' },
      { label: 'Yes — more than 3 years ago', value: 'older' },
      { label: 'No', value: 'none' },
    ],
    required: true,
    field_path: 'epli.ada_audit',
    conditions: EPLI_CONDITION,
    tags: ['epli', 'lob'],
  },
  {
    id: 'epli_eviction_filings',
    text: 'Eviction filings in the last 12 months (all properties)',
    section: 'employees',
    group: 'epli',
    sort_order: 710,
    field_type: 'number',
    placeholder: '5',
    required: true,
    field_path: 'epli.eviction_filings_12mo',
    conditions: EPLI_CONDITION,
    tags: ['epli', 'lob'],
  },
  {
    id: 'epli_mass_layoff',
    text: 'Any mass layoff or reduction-in-force activity in the last 24 months?',
    section: 'employees',
    group: 'epli',
    sort_order: 720,
    field_type: 'boolean',
    required: true,
    field_path: 'epli.mass_layoff',
    conditions: EPLI_CONDITION,
    tags: ['epli', 'lob'],
  },
];
